<template>
    <div>
        <div class="filters">
            <input
                v-model="search"
                :placeholder="$t('Suchen')"
                class="filter-input"
            />

            <select
                v-model="scopeFilter"
                class="filter-select"
            >
                <option value="">
                    {{ $t('Alle Geltungsbereiche') }}
                </option>
                <option
                    v-for="s in uniqueScopes"
                    :key="s"
                    :value="s"
                >
                    {{ s }}
                </option>
            </select>
        </div>
        <div class="table-container">
            <table class="rights-table">
                <thead>
                    <tr>
                        <th>{{ $t('Berechtigung') }}</th>
                        <th
                            v-for="role in roles"
                            :key="role.id"
                        >
                            {{ role.name }}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr
                        v-for="right in filteredRights"
                        :key="right.id"
                        :class="{
                            'hovered-group': hoverBaseRight === right.baseRight,
                            'hovered-current': hoverCurrentId === right.id,
                        }"
                        @mouseover="hoverBaseRight = right.baseRight; hoverCurrentId = right.id"
                        @mouseleave="hoverBaseRight = ''; hoverCurrentId = null"
                    >
                        <td>
                            {{ right.right }}
                            <span
                                class="scope-badge"
                                :class="scopeClassMap[right.scope] || 'scope--default'"
                            >
                                {{ right.scope }}
                            </span>
                        </td>

                        <td
                            v-for="role in roles"
                            :key="role.id"
                            class="matrix-cell"
                        >
                            <input
                                type="checkbox"
                                :checked="role.rights[right.id]"
                                disabled
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    data() {
        return {
            loading: false,
            search: '',
            scopeFilter: '',
            rights: [],
            roles: [],
            hoverBaseRight: '',
            hoverCurrentId: null,
        };
    },
    computed: {
        filteredRights() {
            return this.rights.filter((r) => {
                const matchesSearch = r.right.toLowerCase().includes(this.search.toLowerCase());
                const matchesScope = this.scopeFilter === '' || r.scope === this.scopeFilter;

                return matchesSearch && matchesScope;
            });
        },
        uniqueScopes() {
            return [...new Set(this.rights.map((r) => r.scope))].sort();
        },
        scopeClassMap() {
            return {
                global: 'scope--global',
                'user.same': 'scope--user-same',
                'team.same': 'scope--team-same',
                'team.contained': 'scope--team-contained',
                'department.same': 'scope--department-same',
                'department.same_and_child_departments': 'scope--department-same-and-child-departments',
                'department_and_team.same_and_child_departments_and_related_teams': 'scope--department-and-team-same-and-child-departments-and-related-teams',
                'department.same_and_child_departments_and_team_departments': 'scope--department-same-and-child-departments-and-team-departments',
                'department.contained_and_child_departments_or_teams': 'scope--contained-and-child-departments-or-teams',
                'offering.assigned_instructor': 'scope--offering-assigned-instructor',
            };
        },
    },
    created() {
        this.loadRoles();
    },
    methods: {
        getBaseRight(rightName) {
            return rightName.replace(/Right.*$/, 'Right');
        },
        loadRoles() {
            this.loading = true;
            axios
                .get('/club/security-matrix/club-role-templates')
                .then((response) => {
                    this.roles = response.data.data.roles;
                    this.rights = response.data.data.rights.map((r) => ({
                        ...r,
                        baseRight: this.getBaseRight(r.right),
                    }));
                })
                .catch(() => this.$notify.error('Daten konnten nicht geladen werden'))
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style scoped lang="scss">
.table-container {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 70vh;
    border: 1px solid #ddd;
}

.rights-table {
    width: 100%;
    border-collapse: collapse;
}

.rights-table th,
.rights-table td {
    border: 1px solid #ccc;
    padding: 6px 10px;
    text-align: center;
}

.rights-table thead th {
    position: sticky;
    top: 0;
    background: white;
    z-index: 5;
    border-bottom: 2px solid #999;
}

.rights-table tbody td:first-child,
.rights-table thead th:first-child {
    position: sticky;
    left: 0;
    background: white;
    z-index: 10;
    border-right: 2px solid #999;
    text-align: left;
}

.rights-table tbody tr {
    transition: background-color 0.15s ease-in-out;
}
.rights-table tbody tr.hovered-group td {
    background-color: #e6f0ff;
}

.rights-table tbody tr.hovered-current td {
    background-color: #cce0ff;
}

.matrix-cell {
    background: #fafafa;
}

.scope-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: var(--kgr-font-weight-semi-bold);
    text-transform: lowercase;
    white-space: nowrap;
}

/* ----- SCOPES ----- */
.scope--global {
    background: #e8f3ff;
    color: #1a73e8;
}
.scope--user-same {
    background: #e9f8f2;
    color: #0a7e55;
}
.scope--team-same {
    background: #f0e6ff;
    color: #6b2bc1;
}
.scope--team-contained {
    background: #efe8ff;
    color: #4e2bbf;
}
.scope--department-same {
    background: #fdf3e0;
    color: #c57a00;
}
.scope--department-same-and-child-departments {
    background: #fef0d5;
    color: #a86500;
}
.scope--contained-and-child-departments-or-teams {
    background: #ffedd9;
    color: #a15400;
}
.scope--offering-assigned-instructor {
    background: #e7f5ff;
    color: #1971c2;
}
.scope--department-and-team-same-and-child-departments-and-related-teams {
    background: #e0f7ff;
    color: #0077b6;
}
.scope--department-same-and-child-departments-and-team-departments {
    background: #d0f0ff;
    color: #005f99;
}
.scope--default {
    background: #eee;
    color: #666;
}
</style>
